import { useQuery } from '@tanstack/react-query';
import { Form, Input, Select, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { ApiError } from '../../lib/api-client';
import { useFormValues } from './useFormValues';
import { useDebounced } from '../../lib/use-debounced';
import {
  CASE_CATEGORIES,
  CASE_CHANNELS,
  CASE_SEVERITIES,
  createCase,
  fetchCustomerLinks,
  fetchCustomerUnits,
  LINKABLE_TYPES,
  searchCustomers,
  type LinkableType,
  type CaseCategory,
  type CaseChannel,
  type CaseSeverity,
} from './api';
import { categoryLabel, channelLabel, severityLabel } from './labels';

const { Text } = Typography;

interface Values {
  customerUid: string;
  channel: CaseChannel;
  category: CaseCategory;
  severity: CaseSeverity;
  subject: string;
  description?: string;
  unitUid?: string;
  linkType?: LinkableType;
  linkUid?: string;
}

/**
 * Opens a case. When `customerUid` is given (from the customer page) the
 * customer is fixed; otherwise the advisor finds one by name or phone. Cases
 * never create customers (spec: found with customer search).
 */
export function NewCaseDrawer({
  open,
  customer,
  onClose,
  onCreated,
}: {
  open: boolean;
  customer?: { uid: string; name: string };
  onClose: () => void;
  onCreated: (uid: string, caseNo: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const [form] = Form.useForm<Values>();
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 250);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const { values, onValuesChange, reset } = useFormValues<Values>();
  const { subject, customerUid: pickedUid, linkType, linkUid } = values;
  const customerUid = customer?.uid ?? pickedUid;

  const units = useQuery({
    queryKey: ['service-desk', 'customer-units', customerUid],
    queryFn: () => fetchCustomerUnits(customerUid as string),
    enabled: open && customerUid !== undefined,
  });
  const links = useQuery({
    queryKey: ['service-desk', 'customer-links', customerUid, linkType],
    queryFn: () => fetchCustomerLinks(customerUid as string, linkType as LinkableType),
    enabled: open && customerUid !== undefined && linkType !== undefined,
  });

  // Changing the customer or the link type invalidates what was chosen under it.
  const changed = (delta: Partial<Values>, all: Values): void => {
    if ('customerUid' in delta) {
      form.setFieldsValue({ unitUid: undefined, linkType: undefined, linkUid: undefined });
      onValuesChange(delta, form.getFieldsValue());
    } else if ('linkType' in delta) {
      form.setFieldsValue({ linkUid: undefined });
      onValuesChange(delta, form.getFieldsValue());
    } else {
      onValuesChange(delta, all);
    }
  };

  const found = useQuery({
    queryKey: ['customers', 'picker', debounced],
    queryFn: () => searchCustomers(debounced),
    enabled: open && customer === undefined && debounced.trim().length >= 2,
  });

  const submit = async (): Promise<void> => {
    const values = await form.validateFields();
    setSubmitting(true);
    setFailure(null);
    try {
      const created = await createCase({
        ...values,
        customerUid: customer?.uid ?? values.customerUid,
        subject: values.subject.trim(),
        ...(values.unitUid !== undefined ? { unitUid: values.unitUid } : {}),
        ...(values.linkType !== undefined && values.linkUid !== undefined
          ? { linkType: values.linkType, linkUid: values.linkUid }
          : {}),
        ...(values.description !== undefined && values.description.trim().length > 0
          ? { description: values.description.trim() }
          : {}),
      });
      form.resetFields();
      reset();
      onCreated(created.uid, created.caseNo);
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : t('serviceDesk.drawer.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionDrawer
      open={open}
      title={t('serviceDesk.newCase.title')}
      submitLabel={t('serviceDesk.newCase.submit')}
      submitting={submitting}
      submitDisabled={(subject ?? '').trim().length < 3 || customerUid === undefined || (linkType !== undefined && linkUid === undefined)}
      consequence={t('serviceDesk.newCase.consequence')}
      onSubmit={() => void submit()}
      onClose={() => {
        setFailure(null);
        onClose();
      }}
      error={failure !== null ? <Text type="danger">{failure}</Text> : undefined}
    >
      <Form
        form={form}
        onValuesChange={changed}
        layout="vertical"
        requiredMark={false}
        initialValues={{ channel: 'PHONE', category: 'COMPLAINT', severity: 'MEDIUM' }}
      >
        {customer === undefined ? (
          <Form.Item
            name="customerUid"
            label={t('serviceDesk.newCase.customer')}
            rules={[{ required: true, message: t('serviceDesk.newCase.customerRequired') }]}
          >
            <Select
              size="large"
              showSearch
              filterOption={false}
              onSearch={setSearch}
              placeholder={t('serviceDesk.newCase.customerSearch')}
              loading={found.isFetching}
              notFoundContent={debounced.trim().length >= 2 ? t('serviceDesk.newCase.customerNone') : null}
              options={(found.data ?? []).map((c) => ({ value: c.uid, label: `${c.name} · ${c.phone}` }))}
            />
          </Form.Item>
        ) : (
          <Form.Item label={t('serviceDesk.newCase.customer')}>
            <Text strong>{customer.name}</Text>
          </Form.Item>
        )}
        <Form.Item name="channel" label={t('serviceDesk.newCase.channel')}>
          <Select size="large" options={CASE_CHANNELS.map((c) => ({ value: c, label: channelLabel(t, c) }))} />
        </Form.Item>
        <Form.Item name="category" label={t('serviceDesk.newCase.category')}>
          <Select size="large" options={CASE_CATEGORIES.map((c) => ({ value: c, label: categoryLabel(t, c) }))} />
        </Form.Item>
        <Form.Item name="severity" label={t('serviceDesk.newCase.severity')}>
          <Select size="large" options={CASE_SEVERITIES.map((c) => ({ value: c, label: severityLabel(t, c) }))} />
        </Form.Item>
        <Form.Item
          name="subject"
          label={t('serviceDesk.newCase.subject')}
          rules={[{ required: true, min: 3, whitespace: true, message: t('serviceDesk.newCase.subjectRequired') }]}
        >
          <Input size="large" maxLength={160} />
        </Form.Item>
        <Form.Item name="unitUid" label={t('serviceDesk.newCase.bike')} extra={t('serviceDesk.newCase.bikeHelp')}>
          <Select
            size="large"
            allowClear
            disabled={customerUid === undefined}
            loading={units.isFetching}
            options={(units.data ?? []).map((u) => ({
              value: u.uid,
              label: [u.model, u.plateNo ?? u.chassisNo].filter((p) => p !== null && p.length > 0).join(' · '),
            }))}
          />
        </Form.Item>
        <Form.Item name="linkType" label={t('serviceDesk.newCase.linkType')}>
          <Select
            size="large"
            allowClear
            disabled={customerUid === undefined}
            options={LINKABLE_TYPES.map((x) => ({ value: x, label: t(`serviceDesk.linkType.${x}`) }))}
          />
        </Form.Item>
        {linkType !== undefined && (
          <Form.Item
            name="linkUid"
            label={t('serviceDesk.newCase.linkDoc')}
            extra={linkUid === undefined ? t('serviceDesk.newCase.linkPickDoc') : undefined}
          >
            <Select
              size="large"
              loading={links.isFetching}
              notFoundContent={t('serviceDesk.newCase.linkNone')}
              options={(links.data ?? []).map((l) => ({ value: l.uid, label: l.label }))}
            />
          </Form.Item>
        )}
        <Form.Item name="description" label={t('serviceDesk.newCase.description')}>
          <Input.TextArea rows={4} maxLength={5000} />
        </Form.Item>
      </Form>
    </ActionDrawer>
  );
}
